/**
 * "412 words · 3 to-dos · 8 sources" — the stats line under a draft. One
 * presentational component for the section editor footer and the Full
 * response reader footer, so the two read the same.
 *
 * Plain `text-meta`, joined by " · ", words first. The word count keeps the
 * editor's over/under-target colour when the caller passes one; nothing else is
 * coloured.
 *
 * - To-dos are hidden at 0 and are a button only when the caller can jump to
 *   the first one.
 * - Sources are always shown when the caller has them ("0 sources" plainly);
 *   they are omitted only when `sources` is not passed at all, i.e. the page
 *   cannot know. A non-zero count opens a list of the cited documents; each
 *   opens the source through the same preview path as an inline citation.
 */

import { useState } from 'react'
import { FileText } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { usePreviewStore } from '@/stores/preview-store'
import { openCitationSource } from '@/lib/citation-preview'
import type { CitedSource, DraftToDosStat } from '@/lib/draft-stats'
import type { CitationRef } from '@/lib/api'
import { cn } from '@/lib/utils'

interface DraftStatsFooterProps {
  /** e.g. "412 words" or "412 / 500 words". */
  words: string
  /** Colour for the word count (over/under target). Defaults to the meta tone. */
  wordsClassName?: string | undefined
  toDos?: DraftToDosStat | undefined
  /** Omit when the page cannot know its sources; `[]` shows "0 sources". */
  sources?: CitedSource[] | undefined
}

const LINK_CLASS =
  'rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

function Dot() {
  // `whitespace-pre`: each flex item trims its own edge spaces, which ate the
  // gaps around the dot and left "words·1 to-do".
  return <span aria-hidden className="whitespace-pre"> · </span>
}

export function DraftStatsFooter({ words, wordsClassName, toDos, sources }: DraftStatsFooterProps) {
  const toDoCount = toDos?.count ?? 0
  const toDoLabel = toDoCount === 1 ? '1 to-do' : `${toDoCount} to-dos`

  return (
    <div className="flex items-center whitespace-nowrap text-meta" data-draft-stats="true">
      <span className={cn('text-xs', wordsClassName ?? 'text-muted-foreground')}>{words}</span>
      {toDos && toDoCount > 0 && (
        <>
          <Dot />
          {toDos.onJump ? (
            <button
              type="button"
              onClick={toDos.onJump}
              title="Go to the first to-do"
              className={LINK_CLASS}
            >
              {toDoLabel}
            </button>
          ) : (
            <span>{toDoLabel}</span>
          )}
        </>
      )}
      {sources && (
        <>
          <Dot />
          <SourcesCount sources={sources} />
        </>
      )}
    </div>
  )
}

function SourcesCount({ sources }: { sources: CitedSource[] }) {
  const [open, setOpen] = useState(false)
  const openPreview = usePreviewStore((s) => s.openPreview)
  const label = sources.length === 1 ? '1 source' : `${sources.length} sources`

  if (sources.length === 0) return <span>{label}</span>

  const handleOpen = (ref: CitationRef, source: CitedSource) => {
    openCitationSource(ref, source.kind === 'library', openPreview)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={LINK_CLASS}>
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="top" className="w-80 p-1">
        <ul aria-label="Cited sources" className="max-h-72 overflow-y-auto">
          {sources.map((source) => {
            const first = source.refs[0]
            if (!first) return null
            return (
              <li key={source.documentId} className="rounded-md px-2 py-1.5 hover:bg-muted">
                <button
                  type="button"
                  onClick={() => handleOpen(first, source)}
                  className="flex w-full min-w-0 items-start gap-2 text-left focus-visible:outline-none"
                >
                  <FileText aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-foreground">{source.documentName}</span>
                    <span className="block text-meta">
                      {source.kind === 'library' ? 'Library document' : 'Tender document'}
                    </span>
                  </span>
                </button>
                {source.pages.length > 1 && (
                  <div className="ml-6 mt-0.5 flex flex-wrap items-center gap-x-1.5 text-meta">
                    <span>Pages</span>
                    {source.pages.map((page) => {
                      const ref = source.refs.find((r) => r.pageNumber === page) ?? first
                      return (
                        <button
                          key={page}
                          type="button"
                          onClick={() => handleOpen(ref, source)}
                          aria-label={`Open ${source.documentName} at page ${page}`}
                          className={cn(LINK_CLASS, 'tabular-nums')}
                        >
                          {page}
                        </button>
                      )
                    })}
                  </div>
                )}
                {source.pages.length === 1 && (
                  <div className="ml-6 text-meta tabular-nums">Page {source.pages[0]}</div>
                )}
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
